import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useEffect, useState, useRef, useContext } from 'react'

import { requestAccessToken } from '../../api/auth.api'
import BankIcon from '../../assets/icons/bank-icon.svg'
import AccountIcon from '../../assets/icons/account-icon.svg'
import ContactsIcon from '../../assets/icons/contacts-icon.svg'
import MoneyGreenIcon from '../../assets/icons/money-icon-green.svg'
import { customersBankAccountType, customersDocumentType } from '../../lib'
import { numberWithCommas, numberWithDots } from '../../utils/common.utils'
import PaymentReferenceIcon from '../../assets/icons/payment-reference-icon.svg'
import { AppStateContext, AuthContext, NotificationsContext } from '../../contexts'
import BackgroundResearchIcon from '../../assets/icons/background-research-icon.svg'
import { BankAccount, Customer, FormError, Payment, SupportTicket } from '../../types'
import { PaymentBackgroundNovelties } from '../payment-to-review-details-management/payment-background-novelties'
import { getPaymentTickets, sendPaymentComplianceRequest, sendPaymentComplianceResponse } from '../../api/payments.api'
import { PaymentPendingRoutingTableRow } from '../payment-pending-routing-details-management/payment-pending-routing-table-row'
import {getAttachmentFilePreSignedURL, getDownloadAttachmentFilePreSignedURL, uploadAttachmentFileToS3, resolveDocumentsAccessToken } from '../../api/documents.api'

export const PaymentWithAlertsDetailsTable = ({ paymentData, additionalInfo, paymentCustomerData }: { additionalInfo: Array<any>; paymentData: Payment | null; paymentCustomerData: Customer | null }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const paymentBackgroundNoveltiesRef = useRef()
  const { isAuth, removeAuth } = useContext(AuthContext)
  const { updateLoadingStatus } = useContext(AppStateContext)
  const { t } = useTranslation(['payments-to-review-management'])
  const { updateNewNotificationStatus } = useContext(NotificationsContext)

  const [errors, setErrors] = useState<FormError>({})
  const [paymentAdditionalData, setPaymentAdditionalData] = useState('-')
  const [paymentTicketsData, setPaymentTicketsData] = useState<Array<SupportTicket>>([])
  const [bankAccountData, setBankAccountData] = useState<Partial<BankAccount> | null | undefined>(null)
  // const [dialogConfirmRejectPaymentIsOpen, setDialogConfirmRejectPaymentIsOpen] = useState<boolean>(false)

  useEffect(() => {
    if (paymentData && paymentCustomerData) {
      handleGetPaymentTickets()

      const banksAccountsDataByCustomer =
        Array.isArray(paymentCustomerData.bankAccountsData) && paymentCustomerData.bankAccountsData.length > 0
          ? paymentCustomerData.bankAccountsData?.find((bankAccount) => {
              if (bankAccount.bankNumber === paymentData.bankAccountNumber) {
                // Esto lo hacemos por el formato para el cliente
                if (bankAccount.bankAccountType === '27') {
                  bankAccount.bankAccountType = '1'
                } else if (bankAccount.bankAccountType === '37') {
                  bankAccount.bankAccountType = '2'
                } else if (bankAccount.bankAccountType === '1') {
                  bankAccount.bankAccountType = '1'
                } else if (bankAccount.bankAccountType === '2') {
                  bankAccount.bankAccountType = '2'
                } else {
                  bankAccount.bankAccountType = '3'
                }

                return bankAccount
              }

              return null
            })
          : paymentData?.bankAccountType && paymentData.bankName && paymentData.bankAccountNumber
            ? {
                bankName: paymentData.bankName,
                bankNumber: paymentData.bankAccountNumber,
                // Esto lo hacemos por el formato para el cliente
                bankAccountType: paymentData.bankAccountType === '27' ? '1' : paymentData.bankAccountType === '37' ? '2' : '3'
              }
            : null

      setBankAccountData(banksAccountsDataByCustomer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentData, paymentCustomerData])

  useEffect(() => {
    if (additionalInfo && paymentCustomerData) {
      const paymentCustomerAdditionalInfo = additionalInfo.filter((data) => paymentCustomerData.customerCompanyPocketsRelated && paymentCustomerData.customerCompanyPocketsRelated.includes(String(data._id)))
      const paymentCustomerAdditionalInfoTexts = paymentCustomerAdditionalInfo.length > 0 ? paymentCustomerAdditionalInfo.map((data) => data.name) : []

      if (paymentCustomerAdditionalInfoTexts.length > 0) {
        setPaymentAdditionalData(paymentCustomerAdditionalInfoTexts.join(', '))
        return
      }

      setPaymentAdditionalData('-')
    }
  }, [additionalInfo, paymentCustomerData])

  const handleGetPaymentTickets = async () => {
    const {
      data: { accessToken }
    } = await requestAccessToken(String(isAuth).replaceAll('"', ''))
    const {
      data: { paymentTickets }
    } = await getPaymentTickets(String(paymentData?._id), accessToken)

    setPaymentTicketsData(paymentTickets)
  }

  const handleDownloadAttachmentFile = async (attachmentFilename: string) => {
    setErrors({})

    try {
      updateLoadingStatus?.(true)
      const accessToken = await resolveDocumentsAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const downloadAttachmentFilePreSignedURL = await getDownloadAttachmentFilePreSignedURL({ attachmentFilename }, accessToken)

      window.open(downloadAttachmentFilePreSignedURL.data, '_blank')
      updateLoadingStatus?.(false)
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      updateLoadingStatus?.(false)
    }
  }

  const handleSendCompliancePaymentRequest = async (requestToSend: string) => {
    setErrors({})

    if (!requestToSend) {
      setErrors({ requestToSend: t('complianceProcess.errors.sendRequest') })
      return
    }

    try {
      updateLoadingStatus?.(true)
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const {
        data: { paymentTicketData }
      } = await sendPaymentComplianceRequest({ paymentId: String(paymentData?._id), requestToSend, from: location.pathname }, accessToken)

      const paymentBackgroundNovelties = paymentBackgroundNoveltiesRef.current as unknown as { handleResetToInitState: () => void }
      paymentBackgroundNovelties.handleResetToInitState()
      const paymentTicketsUpdatedData = [...paymentTicketsData]

      if (paymentTicketsUpdatedData.length === 0) {
        paymentTicketsUpdatedData.push(paymentTicketData)
        setPaymentTicketsData(paymentTicketsUpdatedData)
      } else {
        const paymentTicketToUpdateIndex = paymentTicketsUpdatedData.findIndex((paymentTicket) => String(paymentTicket.entityId) === String(paymentTicketData.entityId))
        paymentTicketsUpdatedData[paymentTicketToUpdateIndex] = { ...paymentTicketData }
        setPaymentTicketsData(paymentTicketsUpdatedData)
      }

      updateNewNotificationStatus?.(true, 'payment-request-added')
      updateLoadingStatus?.(false)
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      updateLoadingStatus?.(false)
    }
  }

  const handleSendCompliancePaymentResponse = async (responseToSend: string, supportTicketAttachments: Array<{ file: File | null; fileType: string; filename: string; base64: string }>) => {
    setErrors({})

    if (!responseToSend) {
      setErrors({ responseToSend: t('complianceProcess.errors.sendResponse') })
      return
    }

    try {
      updateLoadingStatus?.(true)
      const supportTicketAttachmentsData = []

      if (supportTicketAttachments.length > 0) {
        for (let i = 0; i < supportTicketAttachments.length; i++) {
          const supportTicketAttachment = supportTicketAttachments[i]

          const attachmentFileData = {
            attachmentFileType: supportTicketAttachment.fileType,
            attachmentFileSystemName: supportTicketAttachment.filename
          }

          const accessToken = await resolveDocumentsAccessToken(isAuth?.replaceAll('"', '') ?? '')
          const attachmentFileToUploadPreSignedURL = await getAttachmentFilePreSignedURL(attachmentFileData, accessToken)
          await uploadAttachmentFileToS3(attachmentFileToUploadPreSignedURL.data, supportTicketAttachment.fileType, supportTicketAttachment.file as File)

          supportTicketAttachmentsData.push({
            filename: supportTicketAttachment.filename,
            fileType: supportTicketAttachment.fileType,
            attachmentFileToUploadPreSignedURL: attachmentFileToUploadPreSignedURL.data
          })
        }
      }

      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const {
        data: { paymentTicketData }
      } = await sendPaymentComplianceResponse({ paymentId: String(paymentData?._id), responseToSend, supportTicketAttachmentsData, from: location.pathname }, accessToken)

      const paymentBackgroundNovelties = paymentBackgroundNoveltiesRef.current as unknown as { handleResetToInitState: () => void }
      paymentBackgroundNovelties.handleResetToInitState()

      const paymentTicketsUpdatedData = [...paymentTicketsData]
      const paymentTicketToUpdateIndex = paymentTicketsUpdatedData.findIndex((paymentTicket) => String(paymentTicket.entityId) === String(paymentTicketData.entityId))
      paymentTicketsUpdatedData[paymentTicketToUpdateIndex] = { ...paymentTicketData }
      setPaymentTicketsData(paymentTicketsUpdatedData)
      updateNewNotificationStatus?.(true, 'payment-request-added')

      updateLoadingStatus?.(false)
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      updateLoadingStatus?.(false)
    }
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <div
      style={{ marginTop: '16px' }}
      className="payment-to-review-details-table-container"
    >
      <div
        style={{ marginTop: '0px' }}
        className="table-header-container"
      >
        <div className="left-content">
          <div className="icon-container">
            <img
              src={AccountIcon}
              alt="account-icon"
            />
          </div>
          <div className="header-info-subtitle">{t('noveltiesTable.customerName')}</div>
        </div>
        <div className="right-content">
          <div className="header-info-data">{paymentData && paymentData.customerRegisteredFullName ? paymentData.customerRegisteredFullName : t('noveltiesTable.unidentified').toUpperCase()}</div>
        </div>
      </div>
      <div className="table-body-container">
        <PaymentPendingRoutingTableRow
          icon={PaymentReferenceIcon}
          subtitle={t('noveltiesTable.referenceNumber')}
          infoData={String(paymentData?.referenceNumber)}
        />
        <PaymentPendingRoutingTableRow
          icon={ContactsIcon}
          subtitle={t('noveltiesTable.idSubtitleName')}
          infoData={
            paymentCustomerData && paymentCustomerData.documentType === 'CE'
              ? `${(customersDocumentType as any)['E']}: ${numberWithDots(String(paymentCustomerData.documentNumber))}`
              : paymentCustomerData && (customersDocumentType as any)[paymentCustomerData.documentType ?? 'C'] !== 'undefined' && numberWithDots(String(paymentCustomerData.documentNumber)) !== 'undefined'
                ? `${(customersDocumentType as any)[paymentCustomerData.documentType ?? 'C']}: ${numberWithDots(String(paymentCustomerData.documentNumber))}`
                : ''
          }
        />
        <PaymentPendingRoutingTableRow
          icon={BankIcon}
          subtitle={t('noveltiesTable.bankAccount')}
          infoData={
            bankAccountData && typeof (customersBankAccountType as any)[bankAccountData.bankAccountType ?? '3'] !== 'undefined' && bankAccountData.bankName !== 'undefined' && bankAccountData.bankNumber !== 'undefined'
              ? `${(customersBankAccountType as any)[bankAccountData.bankAccountType ?? '3']} | ${bankAccountData.bankName} - No. ${bankAccountData.bankNumber}`
              : ''
          }
        />
        <PaymentPendingRoutingTableRow
          icon={MoneyGreenIcon}
          subtitle={t('noveltiesTable.amountToPay')}
          infoData={paymentData?.amount && !isNaN(Number(paymentData?.amount)) && Number(paymentData?.amount) > 0 ? `$ ${numberWithCommas(String(paymentData?.amount))} COP` : ''}
        />
        {paymentAdditionalData && paymentAdditionalData !== '-' && (
          <PaymentPendingRoutingTableRow
            icon={BackgroundResearchIcon}
            infoData={paymentAdditionalData}
            subtitle={t('noveltiesTable.additionalInfo')}
          />
        )}
        <PaymentBackgroundNovelties
          errors={errors}
          paymentData={paymentData}
          ref={paymentBackgroundNoveltiesRef}
          paymentTicketsData={paymentTicketsData}
          handleDownloadAttachmentFile={handleDownloadAttachmentFile}
          handleSendCompliancePaymentRequest={handleSendCompliancePaymentRequest}
          handleSendCompliancePaymentResponse={handleSendCompliancePaymentResponse}
          // handleOpenRejectPaymentModal={() => setDialogConfirmRejectPaymentIsOpen(true)}
        />
      </div>
    </div>
  )
}
